import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { MARKET_VALUE_FILTERS } from '../../../Constants/Interest/Filter';
import { Account, ClientType, ConstructionType, EnergyLabel, LoanForm, NHG } from '../../../Enum/Filters/';
import { InterestTableDTO } from '../../../Interface/Hook/Interest/InterestTableDTO';
import { InterestTableState, INITIAL_TABLE_STATE } from '../../../Interface/Hook/Interest/InterestTableState';
import { ErrorResponse } from '../../../Interface/ErrorResponse';

import { filterFinishedLoading, filterSubmitted } from '../../../Store/Filter/action';
import { getInterestsByMarketValue } from '../../../Service/Interest/InterestService';
import reduceTableByMarketValueData from '../../../Reducer/ReduceTableByMarketValueData';
import { tokenExpired } from '../../../Store/Auth/action';
import useFilterIsActual from '../UseFilterIsActual';

const useGetInterestByMarketValue = (
    constructionType: ConstructionType,
    energyLabel: EnergyLabel,
    loanForm: LoanForm,
    periodIdentifier: string,
    nhg: NHG,
    clientType: ClientType,
    account: Account,
): InterestTableDTO => {
    const dispatch = useDispatch();
    const filterIsActual = useFilterIsActual(MARKET_VALUE_FILTERS);
    const [{ error, data, headers, loading }, setState] = useState<InterestTableState>(INITIAL_TABLE_STATE);

    const fetchData = useCallback(() => {
        if (!filterIsActual) dispatch(filterSubmitted());
        if (filterIsActual)
            getInterestsByMarketValue(
                loanForm,
                periodIdentifier,
                energyLabel,
                constructionType,
                nhg,
                clientType,
                account,
            ).then(
                result => {
                    const marketValueTableData = reduceTableByMarketValueData(result);
                    setState({ error: false, ...marketValueTableData, loading: false });
                    dispatch(filterFinishedLoading());
                },
                (errorResult: ErrorResponse) => {
                    errorResult.code === 403
                        ? dispatch(tokenExpired())
                        : setState({ error: true, data, headers, loading: false });
                    dispatch(filterFinishedLoading());
                },
            );
    }, [
        dispatch,
        constructionType,
        loanForm,
        periodIdentifier,
        energyLabel,
        filterIsActual,
        nhg,
        data,
        headers,
        clientType,
        account,
    ]);

    useEffect(
        () => fetchData(),
        [loanForm, periodIdentifier, energyLabel, constructionType, nhg, clientType, account]
    );

    return { data, error, headers, loading };
};

export default useGetInterestByMarketValue;
